import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import firebaseApp from '../../firebase';

const functions = getFunctions(firebaseApp, 'us-central1');

function daysAgoKey(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

const AdminControls = () => {
  const [backfilling, setBackfilling] = useState(false);
  const [rollingUp, setRollingUp] = useState(false);
  const [rollupProgress, setRollupProgress] = useState(null);
  const [ascDate, setAscDate] = useState(daysAgoKey(1));
  const [ingestingAsc, setIngestingAsc] = useState(false);
  const [playDate, setPlayDate] = useState(daysAgoKey(2));
  const [ingestingPlay, setIngestingPlay] = useState(false);
  const [backfillingPlatforms, setBackfillingPlatforms] = useState(false);
  const [backfillingEmails, setBackfillingEmails] = useState(false);
  const [backfillingCreatedAt, setBackfillingCreatedAt] = useState(false);

  const runBackfill = async () => {
    if (backfilling) return;
    setBackfilling(true);
    try {
      const fn = httpsCallable(functions, 'backfillMetricsTotals');
      const { data } = await fn({});
      toast.success(
        `Seeded _totals: ${data.totals.usersCount} users, ${data.totals.expensesCount} expenses`
      );
    } catch (err) {
      console.error(err);
      toast.error(`Backfill failed: ${err.message}`);
    } finally {
      setBackfilling(false);
    }
  };

  // Sequential so we don't hammer the function with parallel invocations, and
  // so we can show progress. 30 days of rollups typically takes ~60-90 seconds.
  const runRollup30Days = async () => {
    if (rollingUp) return;
    setRollingUp(true);
    const fn = httpsCallable(functions, 'runRollupNow');
    const dates = [];
    for (let i = 30; i >= 1; i -= 1) dates.push(daysAgoKey(i));

    let ok = 0;
    let failed = 0;
    for (let i = 0; i < dates.length; i += 1) {
      const date = dates[i];
      setRollupProgress({ current: i + 1, total: dates.length, date });
      try {
        await fn({ date });
        ok += 1;
      } catch (err) {
        console.error(`rollup failed for ${date}`, err);
        failed += 1;
      }
    }
    setRollupProgress(null);
    setRollingUp(false);
    if (failed === 0) {
      toast.success(`Rolled up ${ok} days`);
    } else {
      toast.error(`Rolled up ${ok} days, ${failed} failed — check console`);
    }
  };

  const runBackfillUserPlatforms = async () => {
    if (backfillingPlatforms) return;
    setBackfillingPlatforms(true);
    try {
      const fn = httpsCallable(functions, 'backfillUserPlatforms');
      const { data } = await fn({});
      toast.success(
        `Inferred platforms for ${data.updated} of ${data.scanned} users (${data.alreadyHadPlatforms} already set)`
      );
    } catch (err) {
      console.error(err);
      toast.error(`Backfill failed: ${err.message}`);
    } finally {
      setBackfillingPlatforms(false);
    }
  };

  const runBackfillEmailStats = async () => {
    if (backfillingEmails) return;
    setBackfillingEmails(true);
    try {
      const fn = httpsCallable(functions, 'backfillEmailStats');
      const { data } = await fn({});
      toast.success(`Rebuilt email stats: ${data.total} sends across ${data.days} days`);
    } catch (err) {
      console.error(err);
      toast.error(`Backfill failed: ${err.message}`);
    } finally {
      setBackfillingEmails(false);
    }
  };

  const runBackfillCreatedAt = async () => {
    if (backfillingCreatedAt) return;
    setBackfillingCreatedAt(true);
    try {
      const fn = httpsCallable(functions, 'backfillUserCreatedAt');
      const { data } = await fn({});
      const daysNote = data.days?.length ? ` · refreshed ${data.rolledUp?.length || 0} day(s)` : '';
      toast.success(
        `Repaired ${data.updated} of ${data.scanned} users${daysNote}` +
          (data.authMissing ? ` (${data.authMissing} had no Auth record)` : '')
      );
    } catch (err) {
      console.error(err);
      toast.error(`Backfill failed: ${err.message}`);
    } finally {
      setBackfillingCreatedAt(false);
    }
  };

  const runAscIngest = async () => {
    if (ingestingAsc) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ascDate)) {
      toast.error('Date must be YYYY-MM-DD');
      return;
    }
    setIngestingAsc(true);
    try {
      const fn = httpsCallable(functions, 'runAscIngestNow');
      const { data } = await fn({ date: ascDate });
      if (data.available) {
        toast.success(
          `iOS ${ascDate}: ${data.firstTime} first-time, ${data.updates} updates`
        );
      } else {
        toast(`No App Store report yet for ${ascDate}`, { icon: 'ℹ️' });
      }
    } catch (err) {
      console.error(err);
      toast.error(`Ingest failed: ${err.message}`);
    } finally {
      setIngestingAsc(false);
    }
  };

  const runPlayIngest = async () => {
    if (ingestingPlay) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(playDate)) {
      toast.error('Date must be YYYY-MM-DD');
      return;
    }
    setIngestingPlay(true);
    try {
      const fn = httpsCallable(functions, 'runPlayIngestNow');
      const { data } = await fn({ date: playDate });
      if (data.available) {
        toast.success(
          `Android ${playDate}: ${data.firstTime} new installs, ${data.updates} updates`
        );
      } else {
        toast(`No Google Play report yet for ${playDate}`, { icon: 'ℹ️' });
      }
    } catch (err) {
      console.error(err);
      toast.error(`Ingest failed: ${err.message}`);
    } finally {
      setIngestingPlay(false);
    }
  };

  return (
    <div className="card p-6 bg-surface-50 border-dashed">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-ink-900 text-[0.9375rem]">Admin controls</h3>
        <span className="text-xs text-ink-400">One-time setup</span>
      </div>
      <p className="text-xs text-ink-500 mb-4">
        Run once after deploy. Backfill seeds running totals from current Firestore data;
        rollup populates the last 30 days of snapshots so charts have history.
      </p>
      <div className="flex flex-wrap gap-3 mb-4">
        <button
          onClick={runBackfill}
          disabled={backfilling}
          className="btn-secondary text-sm"
        >
          {backfilling ? 'Seeding…' : 'Seed totals'}
        </button>
        <button
          onClick={runRollup30Days}
          disabled={rollingUp}
          className="btn-secondary text-sm"
        >
          {rollingUp
            ? rollupProgress
              ? `Rolling up ${rollupProgress.date} (${rollupProgress.current}/${rollupProgress.total})`
              : 'Rolling up…'
            : 'Backfill 30 days of rollups'}
        </button>
        <button
          onClick={runBackfillUserPlatforms}
          disabled={backfillingPlatforms}
          className="btn-secondary text-sm"
          title="Infer platforms.{web|ios} for users registered before the session-ping hook shipped — populates platform-split data"
        >
          {backfillingPlatforms ? 'Inferring…' : 'Backfill user platforms'}
        </button>
        <button
          onClick={runBackfillEmailStats}
          disabled={backfillingEmails}
          className="btn-secondary text-sm"
          title="Rebuild emailStats/* from scheduledEmails and invites — populates the emails-sent chart with history from before the send counters shipped"
        >
          {backfillingEmails ? 'Rebuilding…' : 'Backfill email stats'}
        </button>
        <button
          onClick={runBackfillCreatedAt}
          disabled={backfillingCreatedAt}
          className="btn-secondary text-sm"
          title="Repair users whose doc is missing createdAt/email/signupPlatform (stubs created by a telemetry write before the signup profile write), then re-run the rollup for the days they touched — fixes new-account undercounting"
        >
          {backfillingCreatedAt ? 'Repairing…' : 'Backfill user createdAt'}
        </button>
      </div>
      <div className="pt-4 border-t border-ink-100">
        <label className="block text-xs text-ink-500 mb-2">
          Ingest App Store Connect downloads for a specific date. The scheduled
          job runs daily at 10:00 UTC; use this to re-fetch a day Apple hadn't
          published yet.
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="date"
            value={ascDate}
            onChange={(e) => setAscDate(e.target.value)}
            disabled={ingestingAsc}
            className="px-3 py-2 rounded border border-ink-200 text-sm"
          />
          <button
            onClick={runAscIngest}
            disabled={ingestingAsc}
            className="btn-secondary text-sm"
          >
            {ingestingAsc ? 'Fetching…' : 'Ingest iOS downloads'}
          </button>
        </div>
      </div>
      <div className="pt-4 border-t border-ink-100">
        <label className="block text-xs text-ink-500 mb-2">
          Ingest Google Play install stats for a specific date. The scheduled
          job runs daily at 11:00 UTC; Play reports lag a day or two, so default
          to two days back and use this to backfill once a day's row publishes.
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="date"
            value={playDate}
            onChange={(e) => setPlayDate(e.target.value)}
            disabled={ingestingPlay}
            className="px-3 py-2 rounded border border-ink-200 text-sm"
          />
          <button
            onClick={runPlayIngest}
            disabled={ingestingPlay}
            className="btn-secondary text-sm"
          >
            {ingestingPlay ? 'Fetching…' : 'Ingest Android downloads'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminControls;
